import EventListeners from "./EventListeners";
import {
    EventListenersList,
    EventResponse,
    EventResponseList,
    EventSubscription,
} from "./types";

class Events {
    /**
     * the regex used to split the event subscriptions
     */
    private eventsRegexSplit = /(?:,| )+/;

    /**
     * Event Listeners Container
     */
    private events = new EventListeners();

    /**
     * Get subscriptions by given event name
     *
     * @param {string} event
     * @returns {EventSubscription[]}
     */
    public get(event: string): EventSubscription[] {
        const subscriptions: EventSubscription[] = this.list()[event] || [];

        return subscriptions;
    }

    /**
     * return
     *
     * @returns {EventListenersList}
     */
    public list(): EventListenersList {
        return this.events.list();
    }

    /**
     * Add new event listener
     *
     * @param {string} event
     * @param {Function} callback
     * @returns {EventSubscription | EventSubscription[]}
     */
    public addEventListener(
        event: string,
        callback: Function
    ): EventSubscription | EventSubscription[] {
        if (event.match(this.eventsRegexSplit)) {
            let subscriptions: EventSubscription[] = [];

            for (let eventName of event.split(this.eventsRegexSplit)) {
                subscriptions.push(this.subscribe(eventName, callback));
            }
            return subscriptions;
        } else {
            return this.subscribe(event, callback);
        }
    }

    /**
     * trigger the given event callbacks
     *
     * @param {string} event
     * @param {any[]} args
     * @returns {EventResponseList[] | EventResponse | undefined}
     */
    public trigger(event: string, ...args: any[]) {        
        if (this.events.isEmpty()) return;

        if (event.match(this.eventsRegexSplit)) {
            let returns: EventResponseList = {};

            for (let eventName of event.split(this.eventsRegexSplit)) {
                const trigger = this.trigger(eventName, ...args);

                if (trigger !== undefined)
                    returns[eventName as string] = trigger as EventResponse;
            }

            return returns;
        }

        const eventSubscriptions = this.events.get(event);

        if (eventSubscriptions.length === 0) return;

        let callbackReturnValue: EventResponse = {
            event: event,
            length: 0,
            results: [],
        };

        for (let eventSubscription of eventSubscriptions) {
            let callbackReturn = eventSubscription.dispatch(...args);

            callbackReturnValue.length++;

            if (callbackReturn !== undefined) {
                callbackReturnValue.results.push(callbackReturn);
            }
        }

        return callbackReturnValue;
    }

    /**
     * Subscribe the event
     *
     * @param {string} event
     * @param {Function} callback
     * @returns {EventSubscription}
     */
    public subscribe(event: string, callback: Function): EventSubscription {
        const eventSubscriptions = this.events.get(event) || [];

        let subscription: EventSubscription = {
            event,
            callback,
            dispatch: (...args) => {
                return callback(...args);
            },
            unsubscribe: () => {
                const index = eventSubscriptions.indexOf(subscription);

                if (index !== -1) {
                    eventSubscriptions.splice(index, 1);
                }
            },
        };

        eventSubscriptions.push(subscription);

        this.events.set(event, eventSubscriptions);

        return subscription;
    }

    /**
     * Clear event(s)
     *
     * @param {string} event
     * @returns {this}
     */
    public off(event: string) {
        for (let eventName of event.split(this.eventsRegexSplit)) {
            this.events.delete(eventName);
        }

        return this;
    }

    /**
     * Alias to off method
     */
    public unsubscribe(events: string) {
        return this.off(events);
    }

    /**
     * Clear event listeners container
     */
    public clear() {
        this.events.clear();
    }

    /**
     * An alias to subscribe
     *
     * @param {string} event
     * @param {Function} callback
     * @returns {void}
     */
    public on(
        event: string,
        callback: Function
    ): EventSubscription | EventSubscription[] {
        return this.addEventListener(event, callback);
    }

    /**
     * An alias to trigger
     *
     * @param {string} event
     * @param {any[]} args
     * @returns {EventResponse | EventResponseList[]}
     */
    public emit(event: string, ...args: any[]) {
        return this.trigger(event, ...args);
    }

    /**
     * Remove all events with the given namespace
     *
     * @param   {string} namespace
     * @returns {Events}
     */
    public unsubscribeNamespace(namespace: string): Events {
        this.events.deleteByNamespace(namespace);

        return this;
    }

    /**
     * List all events by namespace
     *
     * @param {string} namespace
     * @returns {EventListenersList}
     */
    public getByNamespace(namespace: string): EventListenersList {
        return this.events.getByNamespace(namespace);
    }
}

const events = new Events();

export default events;
